import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL, ApiError } from '../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { IconGoogle, IconMicrosoft } from '../components/icons';
import { usePublicBranding } from '../hooks/usePublicBranding';
import vnpfLogo from '../assets/vnpf-logo.png';
import './Login.css';

const SSO_ERROR_MESSAGES: Record<string, string> = {
  no_account: 'No account was found for that email. Ask an admin to create one first.',
  failed: 'Sign-in failed. Please try again.',
};

export function Login() {
  const { login, loginTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const branding = usePublicBranding();

  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(SSO_ERROR_MESSAGES[ssoError] ?? SSO_ERROR_MESSAGES.failed);
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.two_factor_required) {
        setChallenge(result.challenge);
        return;
      }
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!challenge) return;
      await loginTwoFactor(challenge, code);
      const redirect = searchParams.get('redirect');
      navigate(redirect || '/');
    } catch (err) {
      setError(err instanceof ApiError ? err.fieldError('code') ?? err.message : 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (challenge) {
    return (
      <div className="login-page">
        <form className="login-card" onSubmit={handleTwoFactorSubmit}>
          <img src={branding.app_icon_url ?? vnpfLogo} alt="" className="brand-mark-lg" />
          <h1>{branding.app_name}</h1>
          <p className="muted">Enter the 6-digit code from your authenticator app.</p>
          {error && <div className="alert alert-error">{error}</div>}
          <label>
            Authentication code
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
            />
            <small className="muted">You can also enter one of your recovery codes.</small>
          </label>
          <button className="btn btn-primary" disabled={loading}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            className="login-secondary-link"
            style={{ background: 'none', border: 0, cursor: 'pointer' }}
            onClick={() => {
              setChallenge(null);
              setCode('');
              setError('');
            }}
          >
            Back to sign in
          </button>
        </form>
      </div>
    );
  }

  const showSso = branding.google_sso_enabled || branding.microsoft_sso_enabled;

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={handleSubmit}>
        <img src={branding.app_icon_url ?? vnpfLogo} alt="" className="brand-mark-lg" />
        <h1>{branding.app_name}</h1>
        {error && <div className="alert alert-error">{error}</div>}
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        </label>
        <label>
          Password
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        <button className="btn btn-primary" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
        <Link to="/forgot-password" className="login-secondary-link">
          Forgot password?
        </Link>

        {showSso && (
          <>
            <div className="login-divider">
              <span>or</span>
            </div>
            {branding.google_sso_enabled && (
              <a className="btn btn-sso" href={`${API_BASE_URL}/auth/google/redirect`}>
                <IconGoogle className="btn-sso-icon" />
                Continue with Google
              </a>
            )}
            {branding.microsoft_sso_enabled && (
              <a className="btn btn-sso" href={`${API_BASE_URL}/auth/microsoft/redirect`}>
                <IconMicrosoft className="btn-sso-icon" />
                Continue with Microsoft
              </a>
            )}
          </>
        )}
      </form>
    </div>
  );
}
